import { useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import {Calculator, TrendingUp, Info} from 'lucide-react';
import { Link } from "react-router-dom";

function NineHoleCalculator() {
  const [grossScore, setGrossScore] = useState("");
  const [courseRating, setCourseRating] = useState("");
  const [slopeRating, setSlopeRating] = useState("113");
  const [result, setResult] = useState<number | null>(null);

  const calculateHandicap = () => {
    const score = parseFloat(grossScore);
    const rating = parseFloat(courseRating);
    const slope = parseFloat(slopeRating);

    if (score && rating && slope && slope > 0) {
      // For 9-hole: multiply by 2 for 18-hole equivalent
      const scoreDifferential = ((score - rating) * 113) / slope;
      setResult(Math.round(scoreDifferential * 10) / 10);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-white to-gray-50 py-8 sm:py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              9-Hole Golf Handicap Calculator
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Quick, mobile-optimized calculator designed for on-course use. Calculate your 9-hole handicap differential instantly.
            </p>
          </div>

          {/* Calculator Card */}
          <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-[#006747] rounded-lg flex items-center justify-center">
                <Calculator className="text-white" size={24} />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">
                9-Hole Score Differential
              </h2>
            </div>

            <div className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Your Gross Score (9 holes)
                </label>
                <input
                  type="number"
                  value={grossScore}
                  onChange={(e) => {
                    setGrossScore(e.target.value);
                    setResult(null);
                  }}
                  placeholder="e.g., 42"
                  className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-lg focus:border-[#006747] focus:outline-none transition-colors"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Course Rating (9 holes)
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={courseRating}
                  onChange={(e) => {
                    setCourseRating(e.target.value);
                    setResult(null);
                  }}
                  placeholder="e.g., 35.5"
                  className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-lg focus:border-[#006747] focus:outline-none transition-colors"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Slope Rating (9 holes)
                </label>
                <input
                  type="number"
                  value={slopeRating}
                  onChange={(e) => {
                    setSlopeRating(e.target.value);
                    setResult(null);
                  }}
                  placeholder="e.g., 113"
                  className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-lg focus:border-[#006747] focus:outline-none transition-colors"
                />
              </div>

              <button
                onClick={calculateHandicap}
                className="w-full bg-[#006747] text-white py-4 px-6 rounded-lg font-semibold text-lg hover:bg-[#005239] transition-colors shadow-lg"
              >
                Calculate 9-Hole Differential
              </button>

              {result !== null ? (
                <div className="bg-[#F0FDF4] border-2 border-[#22C55E] rounded-lg p-6 animate-in fade-in slide-in-from-bottom-4 duration-300">
                  <div className="flex items-center gap-3 mb-2">
                    <TrendingUp className="text-[#006747]" size={28} />
                    <h3 className="text-lg font-semibold text-gray-900">
                      Your Score Differential:
                    </h3>
                  </div>
                  <p className="text-4xl font-bold text-[#006747] mb-3">
                    {result}
                  </p>
                  <p className="text-sm text-gray-600">
                    This represents your performance for this 9-hole round. Track multiple rounds to establish your handicap index.
                  </p>
                  <Link
                    to="/formulas"
                    className="inline-flex items-center gap-2 text-[#006747] font-medium mt-3 hover:underline"
                  >
                    <Info size={16} />
                    How was this calculated?
                  </Link>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </section>

      {/* Info Section */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">
            Understanding 9-Hole Handicaps
          </h2>
          
          <div className="space-y-6 text-gray-700">
            <div className="bg-gray-50 rounded-lg p-6 border-l-4 border-[#006747]">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                What is a 9-Hole Score Differential?
              </h3>
              <p className="mb-3">
                The 9-hole score differential uses the same USGA formula as 18-hole rounds, but with 9-hole specific course and slope ratings. This allows you to track your performance for shorter rounds.
              </p>
              <div className="bg-white p-4 rounded border border-gray-200">
                <code className="text-gray-800 font-mono text-sm">
                  Score Differential = (Score - Course Rating) × 113 / Slope Rating
                </code>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-gray-50 rounded-lg p-5">
                <h4 className="font-semibold text-gray-900 mb-2">
                  ✓ When to Use
                </h4>
                <ul className="text-sm space-y-1 text-gray-600">
                  <li>• Playing 9-hole rounds</li>
                  <li>• Quick after-work golf</li>
                  <li>• Executive courses</li>
                  <li>• Practice round tracking</li>
                </ul>
              </div>

              <div className="bg-gray-50 rounded-lg p-5">
                <h4 className="font-semibold text-gray-900 mb-2">
                  📱 Mobile Optimized
                </h4>
                <ul className="text-sm space-y-1 text-gray-600">
                  <li>• Use on the course</li>
                  <li>• Quick input fields</li>
                  <li>• Instant calculations</li>
                  <li>• No signup required</li>
                </ul>
              </div>
            </div>

            <div className="bg-[#F0FDF4] rounded-lg p-6 border border-[#22C55E]">
              <h4 className="font-semibold text-gray-900 mb-2">
                💡 Pro Tip: Building Your Handicap Index
              </h4>
              <p className="text-gray-700">
                To establish an official handicap index, you need a minimum of 3 score differentials (can mix 9-hole and 18-hole rounds). Two 9-hole differentials equal one 18-hole differential for handicap index calculation.
              </p>
            </div>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row gap-4">
            <Link
              to="/"
              className="flex-1 bg-[#006747] text-white text-center py-3 px-6 rounded-lg font-semibold hover:bg-[#005239] transition-colors"
            >
              Try Full 18-Hole Calculator
            </Link>
            <Link
              to="/formulas"
              className="flex-1 bg-white text-[#006747] text-center py-3 px-6 rounded-lg font-semibold border-2 border-[#006747] hover:bg-gray-50 transition-colors"
            >
              Learn About Formulas
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default NineHoleCalculator;
